import React, { useEffect, useState, useCallback } from 'react';
import BackDeskLayout from '../../components/layout/BackDeskLayout';
import { api } from '../../services/api';
import { useRealtime } from '../../hooks/useRealtime';
import { Button } from '../../components/ui';

/* ── Constantes ─────────────────────────────────────────────────── */
const ACCENT = '#0085CA';

const CAT_LABEL = { laptop:'Portátil', projector:'Proyector', tablet:'Tablet', cable:'Cable', other:'Otro' };
const CAT_ICON  = { laptop:'💻', projector:'📽️', tablet:'📱', cable:'🔌', other:'📦' };
const CAT_COLOR = {
  laptop:    { bg:'#E8F0FE', text:'#0085CA' },
  projector: { bg:'#FFF3E0', text:'#B45309' },
  tablet:    { bg:'#F0FDF4', text:'#15803D' },
  cable:     { bg:'#F5F5F5', text:'#6B7280' },
  other:     { bg:'#F5F5F5', text:'#6B7280' },
};
const ST_LABEL = {
  available:'Disponible', loaned:'Prestado', reserved:'Reservado',
  maintenance:'Mantenimiento', retired:'Retirado',
  pending:'Pendiente', approved:'Aprobado', active:'Activo',
  returned:'Devuelto', overdue:'Vencido', rejected:'Rechazado',
};
const ST_COLOR = {
  available:   { bg:'#E8F5E9', text:'#15803D' },
  loaned:      { bg:'#E8F0FE', text:'#0085CA' },
  reserved:    { bg:'#FFF3E0', text:'#B45309' },
  maintenance: { bg:'#FEF3C7', text:'#B45309' },
  retired:     { bg:'#F5F5F5', text:'#6B7280' },
  pending:     { bg:'#FEF3C7', text:'#B45309' },
  approved:    { bg:'#E8F0FE', text:'#0085CA' },
  active:      { bg:'#DCFCE7', text:'#15803D' },
  returned:    { bg:'#F5F5F5', text:'#6B7280' },
  overdue:     { bg:'#FFEBEE', text:'#B91C1C' },
  rejected:    { bg:'#FFEBEE', text:'#B91C1C' },
};

const inp = {
  width:'100%', padding:'8px 10px', border:'1px solid #E5E7EB',
  borderRadius:7, fontSize:13, boxSizing:'border-box', outline:'none',
  fontFamily:'inherit',
};

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('es-ES', { day:'2-digit', month:'short', year:'numeric' });
}

/* ── Helpers UI ─────────────────────────────────────────────────── */
function Badge({ status, size=11 }) {
  const c = ST_COLOR[status] || { bg:'#F5F5F5', text:'#6B7280' };
  return <span style={{ fontSize:size, fontWeight:600, padding:'2px 8px', borderRadius:10, background:c.bg, color:c.text }}>{ST_LABEL[status] || status}</span>;
}
function CatBadge({ category }) {
  const c = CAT_COLOR[category] || { bg:'#F5F5F5', text:'#6B7280' };
  return <span style={{ fontSize:10, fontWeight:600, padding:'2px 7px', borderRadius:8, background:c.bg, color:c.text }}>{CAT_ICON[category]} {CAT_LABEL[category] || category}</span>;
}
function EqImg({ url, size=48 }) {
  if (url) return <img src={url} alt="" style={{ width:size, height:size, borderRadius:8, objectFit:'cover', border:'1px solid #E5E7EB', flexShrink:0 }} onError={e => e.target.style.display='none'}/>;
  return <div style={{ width:size, height:size, borderRadius:8, background:'#F3F4F6', border:'1px solid #E5E7EB', display:'flex', alignItems:'center', justifyContent:'center', fontSize:size*0.45, flexShrink:0 }}>📦</div>;
}
function Btn({ onClick, color='#374151', bg='#fff', border='#E5E7EB', children, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{ padding:'5px 12px', borderRadius:7, border:`1px solid ${border}`, background:bg, color, fontSize:11, fontWeight:600, cursor:disabled?'default':'pointer', opacity:disabled?.6:1, fontFamily:'inherit' }}>
      {children}
    </button>
  );
}

/* ── KPI card ───────────────────────────────────────────────────── */
function KPI({ label, value, color, bg, icon }) {
  return (
    <div style={{ flex:1, minWidth:130, padding:'14px 18px', borderRadius:12, background:bg, border:`1px solid ${color}25`, display:'flex', alignItems:'center', gap:14 }}>
      <div style={{ fontSize:26 }}>{icon}</div>
      <div>
        <div style={{ fontSize:22, fontWeight:800, color, lineHeight:1 }}>{value}</div>
        <div style={{ fontSize:11, color, fontWeight:600, marginTop:3, opacity:.8 }}>{label}</div>
      </div>
    </div>
  );
}

/* ── Modal genérico ─────────────────────────────────────────────── */
function Modal({ title, onClose, children, footer, width=500 }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.4)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
      <div style={{ background:'#fff', borderRadius:14, width, maxWidth:'100%', maxHeight:'92vh', display:'flex', flexDirection:'column', boxShadow:'0 12px 48px rgba(0,0,0,.18)', overflow:'hidden' }}>
        <div style={{ padding:'18px 22px', borderBottom:'1px solid #F3F4F6', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
          <span style={{ fontSize:15, fontWeight:700, color:'#111827' }}>{title}</span>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', fontSize:22, color:'#9CA3AF', lineHeight:1, padding:0 }}>×</button>
        </div>
        <div style={{ padding:'20px 22px', overflowY:'auto', flex:1 }}>{children}</div>
        {footer && (
          <div style={{ padding:'14px 22px', borderTop:'1px solid #F3F4F6', display:'flex', justifyContent:'flex-end', gap:8, flexShrink:0 }}>{footer}</div>
        )}
      </div>
    </div>
  );
}
function Field({ label, children }) {
  return (
    <div style={{ marginBottom:14 }}>
      <label style={{ fontSize:11, fontWeight:600, color:'#6B7280', textTransform:'uppercase', letterSpacing:.4, display:'block', marginBottom:5 }}>{label}</label>
      {children}
    </div>
  );
}

/* ── Modal equipo ───────────────────────────────────────────────── */
function EquipmentModal({ initial, onSave, onClose }) {
  const [form, setForm] = useState({
    name:initial?.name||'', category:initial?.category||'other',
    brand:initial?.brand||'', model:initial?.model||'',
    serial_number:initial?.serial_number||'', status:initial?.status||'available',
    description:initial?.description||'', image_url:initial?.image_url||'',
  });
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const set = k => e => setForm(f => ({ ...f, [k]:e.target.value }));

  const handleSave = async () => {
    if (!form.name.trim()) { setErr('El nombre es obligatorio'); return; }
    setSaving(true); setErr('');
    try { await onSave(form); onClose(); }
    catch(e) { setErr(e.message); setSaving(false); }
  };

  return (
    <Modal title={initial ? 'Editar equipo' : 'Nuevo equipo'} onClose={onClose}
      footer={<>
        <Button variant="secondary" onClick={onClose}>Cancelar</Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>{saving?'Guardando…':initial?'Guardar':'Crear'}</Button>
      </>}
    >
      <Field label="Nombre *">
        <input style={inp} value={form.name} onChange={set('name')} placeholder="Ej: Portátil Dell XPS 15"/>
      </Field>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
        <Field label="Categoría">
          <select style={inp} value={form.category} onChange={set('category')}>
            {Object.entries(CAT_LABEL).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </Field>
        <Field label="Estado">
          <select style={inp} value={form.status} onChange={set('status')}>
            <option value="available">Disponible</option>
            <option value="maintenance">Mantenimiento</option>
            <option value="retired">Retirado</option>
          </select>
        </Field>
        <Field label="Marca">
          <input style={inp} value={form.brand} onChange={set('brand')} placeholder="Ej: Dell"/>
        </Field>
        <Field label="Modelo">
          <input style={inp} value={form.model} onChange={set('model')} placeholder="Ej: XPS 15"/>
        </Field>
      </div>
      <Field label="Número de serie">
        <input style={inp} value={form.serial_number} onChange={set('serial_number')} placeholder="Ej: SN-12345"/>
      </Field>
      <Field label="URL de imagen (opcional)">
        <input style={inp} value={form.image_url} onChange={set('image_url')} placeholder="https://…"/>
        {form.image_url && <img src={form.image_url} alt="" style={{ marginTop:6, height:60, borderRadius:6, border:'1px solid #E5E7EB' }} onError={e=>e.target.style.display='none'}/>}
      </Field>
      <Field label="Descripción (opcional)">
        <textarea style={{ ...inp, resize:'vertical', minHeight:56 }} value={form.description} onChange={set('description')} placeholder="Descripción breve…"/>
      </Field>
      {err && <div style={{ fontSize:12, color:'#B91C1C', background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:6, padding:'6px 10px' }}>{err}</div>}
    </Modal>
  );
}

/* ── Modal aprobación ───────────────────────────────────────────── */
function ApproveModal({ loan, onConfirm, onClose }) {
  const today = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState({ equipment_id: loan.equipment_id||'', loan_date:loan.loan_date||today, expected_return:loan.expected_return||'', notes:loan.notes||'' });
  const [available, setAvailable] = useState([]);
  const [loadingEq, setLoadingEq] = useState(true);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const set = k => e => setForm(f => ({ ...f, [k]:e.target.value }));

  useEffect(() => {
    setLoadingEq(true);
    const p = new URLSearchParams({ status:'available', limit:100 });
    if (loan.requested_category) p.set('category', loan.requested_category);
    api.getEquipment('?'+p).then(setAvailable).catch(()=>{}).finally(()=>setLoadingEq(false));
  }, [loan.requested_category]);

  const handleConfirm = async () => {
    if (!form.equipment_id) { setErr('Asigna un equipo concreto antes de aprobar'); return; }
    if (!form.expected_return) { setErr('La fecha de devolución es obligatoria'); return; }
    setSaving(true); setErr('');
    try { await onConfirm({ status:'approved', ...form, equipment_id:Number(form.equipment_id) }); onClose(); }
    catch(e) { setErr(e.message); setSaving(false); }
  };

  return (
    <Modal title="Aprobar préstamo" onClose={onClose}
      footer={<>
        <Button variant="secondary" onClick={onClose}>Cancelar</Button>
        <Button variant="success" onClick={handleConfirm} disabled={saving}>{saving?'Aprobando…':'✓ Aprobar préstamo'}</Button>
      </>}
    >
      {/* Info solicitante */}
      <div style={{ background:'#F9FAFB', borderRadius:10, padding:'12px 16px', marginBottom:18 }}>
        <div style={{ fontWeight:700, color:'#111827', fontSize:14 }}>
          {loan.requested_category ? CAT_LABEL[loan.requested_category] || loan.requested_category : 'Equipo sin especificar'}
        </div>
        <div style={{ fontSize:12, color:'#6B7280', marginTop:2 }}>{loan.requester_name} · {loan.requester_email}</div>
        {loan.purpose && <div style={{ fontSize:12, color:'#374151', marginTop:4, fontStyle:'italic' }}>"{loan.purpose}"</div>}
      </div>

      <Field label="Asignar equipo concreto *">
        {loadingEq ? (
          <div style={{ fontSize:12, color:'#9CA3AF', padding:'8px 0' }}>Cargando equipos disponibles…</div>
        ) : available.length === 0 ? (
          <div style={{ fontSize:12, color:'#B45309', background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:7, padding:'8px 10px' }}>
            No hay equipos disponibles{loan.requested_category ? ` de tipo "${CAT_LABEL[loan.requested_category]||loan.requested_category}"` : ''} ahora mismo. Da de alta uno en el Inventario antes de aprobar.
          </div>
        ) : (
          <select style={inp} value={form.equipment_id} onChange={set('equipment_id')}>
            <option value="">Selecciona un equipo…</option>
            {available.map(eq => (
              <option key={eq.id} value={eq.id}>{eq.name}{eq.serial_number ? ` — ${eq.serial_number}` : ''}</option>
            ))}
          </select>
        )}
      </Field>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
        <Field label="Fecha de préstamo">
          <input style={inp} type="date" value={form.loan_date} onChange={set('loan_date')}/>
        </Field>
        <Field label="Fecha de devolución *">
          <input style={inp} type="date" value={form.expected_return} onChange={set('expected_return')} min={form.loan_date}/>
        </Field>
      </div>
      <Field label="Notas internas">
        <textarea style={{ ...inp, resize:'vertical', minHeight:60 }} value={form.notes} onChange={set('notes')} placeholder="Notas opcionales para el registro…"/>
      </Field>
      {err && <div style={{ fontSize:12, color:'#B91C1C', background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:6, padding:'6px 10px' }}>{err}</div>}
    </Modal>
  );
}

/* ── Tarjeta equipo (inventario) ────────────────────────────────── */
function EquipmentCard({ eq, onEdit, onRetire }) {
  return (
    <div style={{
      background:'#fff', border:'1px solid #E5E7EB', borderRadius:12,
      display:'flex', flexDirection:'column', overflow:'hidden',
      transition:'box-shadow .15s',
    }}
      onMouseEnter={e => e.currentTarget.style.boxShadow='0 4px 16px rgba(0,0,0,.08)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow='none'}
    >
      {/* Cabecera de color según categoría */}
      <div style={{ height:6, background: eq.status==='available'?'#15803D': eq.status==='loaned'?ACCENT: eq.status==='maintenance'?'#B45309':'#E5E7EB' }}/>

      <div style={{ padding:'16px 16px 12px', flex:1 }}>
        {/* Imagen + nombre */}
        <div style={{ display:'flex', gap:12, marginBottom:12 }}>
          <EqImg url={eq.image_url} size={52}/>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontWeight:700, color:'#111827', fontSize:14, lineHeight:1.3, marginBottom:6, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{eq.name}</div>
            <div style={{ display:'flex', gap:4, flexWrap:'wrap' }}>
              <CatBadge category={eq.category}/>
              <Badge status={eq.status} size={10}/>
            </div>
          </div>
        </div>

        {/* Datos */}
        <div style={{ fontSize:12, color:'#6B7280', display:'flex', flexDirection:'column', gap:4 }}>
          {(eq.brand || eq.model) && (
            <div style={{ display:'flex', gap:4 }}>
              <span style={{ color:'#9CA3AF' }}>Marca/Modelo</span>
              <span style={{ color:'#374151', fontWeight:500 }}>{[eq.brand, eq.model].filter(Boolean).join(' ')}</span>
            </div>
          )}
          {eq.serial_number && (
            <div style={{ display:'flex', gap:4 }}>
              <span style={{ color:'#9CA3AF' }}>Serie</span>
              <span style={{ color:'#374151', fontFamily:'monospace', fontSize:11 }}>{eq.serial_number}</span>
            </div>
          )}
          {eq.total_loans > 0 && (
            <div style={{ color:'#9CA3AF' }}>{eq.total_loans} préstamo{eq.total_loans>1?'s':''} total{eq.total_loans>1?'es':''}</div>
          )}
        </div>
      </div>

      {/* Acciones */}
      <div style={{ padding:'10px 16px', borderTop:'1px solid #F3F4F6', display:'flex', gap:6, justifyContent:'flex-end' }}>
        <Button variant="secondary" size="sm" onClick={() => onEdit(eq)}>✏️ Editar</Button>
        {eq.status !== 'retired' && (
          <Button variant="danger" size="sm" onClick={() => onRetire(eq)}>Retirar</Button>
        )}
      </div>
    </div>
  );
}

/* ── Tarjeta préstamo (activos) ─────────────────────────────────── */
function LoanCard({ loan, onApprove, onActivate, onReturn, onReject }) {
  const isOverdue = loan.is_overdue || loan.status === 'overdue';

  // Calcular % de tiempo transcurrido
  let pct = 0;
  if (loan.loan_date && loan.expected_return && loan.status === 'active') {
    const start = new Date(loan.loan_date).getTime();
    const end   = new Date(loan.expected_return).getTime();
    const now   = Date.now();
    pct = Math.min(100, Math.max(0, Math.round((now - start) / (end - start) * 100)));
  }

  return (
    <div style={{
      border:`1px solid ${isOverdue?'#FECACA':'#E5E7EB'}`,
      borderLeft:`4px solid ${
        isOverdue?'#B91C1C':
        loan.status==='pending'?'#B45309':
        loan.status==='approved'?ACCENT:
        loan.status==='active'?'#15803D':'#E5E7EB'
      }`,
      borderRadius:10, padding:'16px 18px',
      background: isOverdue ? '#FFF5F5' : '#fff',
    }}>
      <div style={{ display:'flex', gap:14, alignItems:'flex-start' }}>
        {/* Equipo (o tipo solicitado, si aún no se ha asignado uno concreto) */}
        {loan.equipment_id ? (
          <EqImg url={loan.equipment_image} size={44}/>
        ) : (
          <div style={{ width:44, height:44, borderRadius:8, background:'#FFFBEB', border:'1px solid #FDE68A', display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, flexShrink:0 }}>
            {CAT_ICON[loan.requested_category] || '❓'}
          </div>
        )}

        {/* Info central */}
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4, flexWrap:'wrap' }}>
            <span style={{ fontWeight:700, color:'#111827', fontSize:14 }}>
              {loan.equipment_id ? (loan.equipment_name || '—') : `Sin asignar — ${CAT_LABEL[loan.requested_category] || loan.requested_category || 'tipo no indicado'}`}
            </span>
            {loan.equipment_id && <CatBadge category={loan.equipment_category}/>}
            <Badge status={loan.status}/>
            {isOverdue && <span style={{ fontSize:10, fontWeight:700, color:'#B91C1C' }}>⚠️ VENCIDO</span>}
          </div>

          <div style={{ display:'flex', gap:16, flexWrap:'wrap', marginBottom:8 }}>
            <div>
              <div style={{ fontSize:10, color:'#9CA3AF', textTransform:'uppercase', letterSpacing:.4, marginBottom:1 }}>Solicitante</div>
              <div style={{ fontSize:12, fontWeight:600, color:'#111827' }}>{loan.requester_name}</div>
              <div style={{ fontSize:11, color:'#6B7280' }}>{loan.requester_email}</div>
            </div>
            {loan.loan_date && (
              <div>
                <div style={{ fontSize:10, color:'#9CA3AF', textTransform:'uppercase', letterSpacing:.4, marginBottom:1 }}>Préstamo</div>
                <div style={{ fontSize:12, color:'#374151' }}>{fmtDate(loan.loan_date)}</div>
              </div>
            )}
            {loan.expected_return && (
              <div>
                <div style={{ fontSize:10, color:'#9CA3AF', textTransform:'uppercase', letterSpacing:.4, marginBottom:1 }}>Devolución</div>
                <div style={{ fontSize:12, color: isOverdue?'#B91C1C':'#374151', fontWeight: isOverdue?700:400 }}>{fmtDate(loan.expected_return)}</div>
              </div>
            )}
            {loan.agent_name && (
              <div>
                <div style={{ fontSize:10, color:'#9CA3AF', textTransform:'uppercase', letterSpacing:.4, marginBottom:1 }}>Agente</div>
                <div style={{ fontSize:12, color:'#374151' }}>{loan.agent_name}</div>
              </div>
            )}
          </div>

          {loan.purpose && (
            <div style={{ fontSize:11, color:'#6B7280', fontStyle:'italic', marginBottom:8 }}>"{loan.purpose.slice(0,80)}{loan.purpose.length>80?'…':''}"</div>
          )}

          {/* Barra de progreso (solo si activo y tiene fechas) */}
          {loan.status === 'active' && loan.loan_date && loan.expected_return && (
            <div style={{ marginBottom:8 }}>
              <div style={{ height:4, background:'#F3F4F6', borderRadius:4, overflow:'hidden' }}>
                <div style={{ height:'100%', width:`${pct}%`, background: pct>85?'#B91C1C':pct>60?'#B45309':ACCENT, borderRadius:4, transition:'width .3s' }}/>
              </div>
              <div style={{ fontSize:10, color:'#9CA3AF', marginTop:2 }}>{pct}% del tiempo transcurrido</div>
            </div>
          )}

          {/* Acciones */}
          <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
            {loan.status === 'pending' && (<>
              <Button variant="success" size="sm" onClick={() => onApprove(loan)}>✓ Aprobar</Button>
              <Button variant="danger" size="sm" onClick={() => onReject(loan)}>✕ Rechazar</Button>
            </>)}
            {loan.status === 'approved' && (<>
              <Btn onClick={() => onActivate(loan)} bg='#EFF6FF' color='#1D4ED8' border='#93C5FD'>▶ Activar entrega</Btn>
              <Button variant="danger" size="sm" onClick={() => onReject(loan)}>✕ Rechazar</Button>
            </>)}
            {loan.status === 'active' && (
              <Button variant="success" size="sm" onClick={() => onReturn(loan)}>↩ Marcar devuelto</Button>
            )}
            {loan.status === 'overdue' && (
              <Button variant="danger" size="sm" onClick={() => onReturn(loan)}>↩ Registrar devolución</Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Tab: Inventario ────────────────────────────────────────────── */
function TabInventario({ onReload }) {
  const [equipment, setEquipment] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [stats,     setStats]     = useState(null);
  const [search,    setSearch]    = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [stFilter,  setStFilter]  = useState('');
  const [modal,     setModal]     = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ limit:200 });
      if (search)    p.set('search',   search);
      if (catFilter) p.set('category', catFilter);
      if (stFilter)  p.set('status',   stFilter);
      const [eq, st] = await Promise.all([api.getEquipment('?'+p), api.getLoanStats()]);
      setEquipment(eq); setStats(st);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [search, catFilter, stFilter]);

  useEffect(() => { load(); }, [load]);

  const handleSave  = async form => { if (modal === 'new') await api.createEquipment(form); else await api.updateEquipment(modal.id, form); load(); };
  const handleRetire = async eq => { if (!window.confirm(`¿Retirar "${eq.name}"?`)) return; try { await api.deleteEquipment(eq.id); load(); } catch(e) { alert(e.message); } };

  const eqSt = {};
  if (stats?.equipment) for (const s of stats.equipment) eqSt[s.status] = s.count;
  const total = Object.values(eqSt).reduce((a,b)=>a+b,0);

  return (<>
    {modal !== null && <EquipmentModal initial={modal==='new'?null:modal} onSave={handleSave} onClose={() => setModal(null)}/>}

    {/* KPIs */}
    <div style={{ display:'flex', gap:12, marginBottom:24, flexWrap:'wrap' }}>
      <KPI label="Total equipos"  value={total}                    color='#374151' bg='#F9FAFB'   icon='🗄️'/>
      <KPI label="Disponibles"    value={eqSt.available||0}        color='#15803D' bg='#E8F5E9'   icon='✅'/>
      <KPI label="Prestados"      value={eqSt.loaned||0}           color='#0085CA' bg='#E8F0FE'   icon='📤'/>
      <KPI label="Mantenimiento"  value={eqSt.maintenance||0}      color='#B45309' bg='#FEF3C7'   icon='🔧'/>
      <KPI label="Vencidos"       value={stats?.overdue||0}        color='#B91C1C' bg='#FFEBEE'   icon='⚠️'/>
    </div>

    {/* Toolbar */}
    <div style={{ display:'flex', gap:8, marginBottom:20, flexWrap:'wrap', alignItems:'center' }}>
      <input style={{ ...inp, flex:1, minWidth:180, maxWidth:300 }} placeholder="Buscar equipo…" value={search} onChange={e=>setSearch(e.target.value)}/>
      <select style={{ ...inp, width:'auto' }} value={catFilter} onChange={e=>setCatFilter(e.target.value)}>
        <option value="">Todas las categorías</option>
        {Object.entries(CAT_LABEL).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
      </select>
      <select style={{ ...inp, width:'auto' }} value={stFilter} onChange={e=>setStFilter(e.target.value)}>
        <option value="">Todos los estados</option>
        <option value="available">Disponible</option>
        <option value="loaned">Prestado</option>
        <option value="maintenance">Mantenimiento</option>
        <option value="retired">Retirado</option>
      </select>
      <Button variant="primary" onClick={() => setModal('new')}>+ Añadir equipo</Button>
    </div>

    {loading ? (
      <div style={{ textAlign:'center', padding:'48px 0', color:'#9CA3AF' }}><div className="bd-spinner-sm" style={{ margin:'0 auto 12px' }}/> Cargando…</div>
    ) : equipment.length === 0 ? (
      <div style={{ textAlign:'center', padding:'56px 0', color:'#9CA3AF' }}>
        <div style={{ fontSize:40, marginBottom:10 }}>📦</div>
        <div style={{ fontSize:14, fontWeight:500 }}>No hay equipos registrados</div>
        <div style={{ fontSize:12, marginTop:4 }}>Añade el primer equipo con el botón de arriba</div>
      </div>
    ) : (
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(260px, 1fr))', gap:14 }}>
        {equipment.map(eq => <EquipmentCard key={eq.id} eq={eq} onEdit={setModal} onRetire={handleRetire}/>)}
      </div>
    )}
  </>);
}

/* ── Tab: Préstamos activos ─────────────────────────────────────── */
function TabActivos({ onReload }) {
  const [loans,   setLoans]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState('');
  const [stFilt,  setStFilt]  = useState('');
  const [approve, setApprove] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const all = (await Promise.all([
        api.getLoans('?status=pending&limit=100'),
        api.getLoans('?status=approved&limit=100'),
        api.getLoans('?status=active&limit=100'),
        api.getLoans('?status=overdue&limit=100'),
      ])).flat().sort((a,b) => new Date(b.created_at)-new Date(a.created_at));
      setLoans(all);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (loan, status) => { try { await api.updateLoan(loan.id,{status}); load(); onReload(); } catch(e) { alert(e.message); } };
  const ret = async loan => { try { await api.returnLoan(loan.id); load(); onReload(); } catch(e) { alert(e.message); } };
  const confirmApprove = async fields => { await api.updateLoan(approve.id, fields); setApprove(null); load(); onReload(); };

  const filtered = loans.filter(l => {
    if (stFilt && l.status !== stFilt) return false;
    if (search) {
      const q = search.toLowerCase();
      return l.requester_name.toLowerCase().includes(q) || l.requester_email.toLowerCase().includes(q) || (l.equipment_name||'').toLowerCase().includes(q);
    }
    return true;
  });

  const counts = { pending:0, approved:0, active:0, overdue:0 };
  for (const l of loans) if (counts[l.status] !== undefined) counts[l.status]++;

  return (<>
    {approve && <ApproveModal loan={approve} onConfirm={confirmApprove} onClose={() => setApprove(null)}/>}

    {/* Mini KPIs de estado */}
    <div style={{ display:'flex', gap:10, marginBottom:20, flexWrap:'wrap' }}>
      {[
        { key:'pending',  label:'Pendientes', color:'#B45309', bg:'#FEF3C7', icon:'🕐' },
        { key:'approved', label:'Aprobados',  color:'#0085CA', bg:'#E8F0FE', icon:'✅' },
        { key:'active',   label:'Activos',    color:'#15803D', bg:'#E8F5E9', icon:'📤' },
        { key:'overdue',  label:'Vencidos',   color:'#B91C1C', bg:'#FFEBEE', icon:'⚠️' },
      ].map(s => (
        <KPI key={s.key} label={s.label} value={counts[s.key]} color={s.color} bg={s.bg} icon={s.icon}/>
      ))}
    </div>

    {/* Filtros */}
    <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap', alignItems:'center' }}>
      <input style={{ ...inp, flex:1, minWidth:180, maxWidth:300 }} placeholder="Buscar solicitante, equipo…" value={search} onChange={e=>setSearch(e.target.value)}/>
      <select style={{ ...inp, width:'auto' }} value={stFilt} onChange={e=>setStFilt(e.target.value)}>
        <option value="">Todos los estados</option>
        <option value="pending">Pendiente</option>
        <option value="approved">Aprobado</option>
        <option value="active">Activo</option>
        <option value="overdue">Vencido</option>
      </select>
      <Button variant="secondary" onClick={load}>↻ Actualizar</Button>
    </div>

    {loading ? (
      <div style={{ textAlign:'center', padding:'48px 0', color:'#9CA3AF' }}><div className="bd-spinner-sm" style={{ margin:'0 auto 12px' }}/> Cargando…</div>
    ) : filtered.length === 0 ? (
      <div style={{ textAlign:'center', padding:'56px 0', color:'#9CA3AF' }}>
        <div style={{ fontSize:40, marginBottom:10 }}>✅</div>
        <div style={{ fontSize:14, fontWeight:500 }}>No hay préstamos activos</div>
      </div>
    ) : (
      <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
        {filtered.map(loan => (
          <LoanCard key={loan.id} loan={loan}
            onApprove={setApprove}
            onActivate={l => act(l,'active')}
            onReturn={ret}
            onReject={l => { if(window.confirm('¿Rechazar este préstamo?')) act(l,'rejected'); }}
          />
        ))}
      </div>
    )}
  </>);
}

/* ── Tab: Historial ─────────────────────────────────────────────── */
function TabHistorial() {
  const [loans,   setLoans]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [stFilt,  setStFilt]  = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const all = (await Promise.all([
        api.getLoans('?status=returned&limit=200'),
        api.getLoans('?status=rejected&limit=200'),
      ])).flat().sort((a,b) => new Date(b.updated_at)-new Date(a.updated_at));
      setLoans(all);
    } catch { }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = stFilt ? loans.filter(l => l.status === stFilt) : loans;

  function duration(l) {
    if (!l.loan_date || !l.actual_return) return '—';
    const d = Math.round((new Date(l.actual_return)-new Date(l.loan_date))/86400000);
    return `${d}d`;
  }

  return (<>
    <div style={{ display:'flex', gap:8, marginBottom:16, alignItems:'center' }}>
      <select style={{ ...inp, width:'auto' }} value={stFilt} onChange={e=>setStFilt(e.target.value)}>
        <option value="">Todos</option>
        <option value="returned">Devueltos</option>
        <option value="rejected">Rechazados</option>
      </select>
      <span style={{ fontSize:12, color:'#9CA3AF' }}>{filtered.length} registro{filtered.length!==1?'s':''}</span>
    </div>

    {loading ? (
      <div style={{ textAlign:'center', padding:'48px 0', color:'#9CA3AF' }}>Cargando…</div>
    ) : filtered.length === 0 ? (
      <div style={{ textAlign:'center', padding:'56px 0', color:'#9CA3AF' }}>
        <div style={{ fontSize:40, marginBottom:10 }}>📋</div>
        <div style={{ fontSize:14 }}>Sin historial todavía</div>
      </div>
    ) : (
      <div style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:10, overflow:'hidden' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
          <thead>
            <tr style={{ background:'#F9FAFB', borderBottom:'1px solid #E5E7EB' }}>
              {['Equipo','Solicitante','Préstamo','Devolución','Duración','Estado','Agente'].map(h => (
                <th key={h} style={{ padding:'10px 14px', textAlign:'left', fontWeight:600, color:'#6B7280', fontSize:11, textTransform:'uppercase', letterSpacing:.4 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((l,i) => (
              <tr key={l.id} style={{ borderBottom: i<filtered.length-1?'1px solid #F3F4F6':'none' }}>
                <td style={{ padding:'10px 14px' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <EqImg url={l.equipment_image} size={30}/>
                    <div>
                      <div style={{ fontWeight:600, color:'#111827' }}>{l.equipment_name||'—'}</div>
                      <CatBadge category={l.equipment_category}/>
                    </div>
                  </div>
                </td>
                <td style={{ padding:'10px 14px' }}>
                  <div style={{ fontWeight:500, color:'#111827' }}>{l.requester_name}</div>
                  <div style={{ color:'#9CA3AF', fontSize:11 }}>{l.requester_email}</div>
                </td>
                <td style={{ padding:'10px 14px', color:'#374151' }}>{fmtDate(l.loan_date)}</td>
                <td style={{ padding:'10px 14px', color:'#374151' }}>{fmtDate(l.actual_return)}</td>
                <td style={{ padding:'10px 14px', color:'#6B7280', fontWeight:600 }}>{duration(l)}</td>
                <td style={{ padding:'10px 14px' }}><Badge status={l.status}/></td>
                <td style={{ padding:'10px 14px', color:'#374151' }}>{l.agent_name||<span style={{color:'#9CA3AF'}}>—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </>);
}

/* ── Componente principal ───────────────────────────────────────── */
const TABS = [
  { key:'inventario', label:'📦 Inventario' },
  { key:'activos',    label:'📤 Préstamos activos' },
  { key:'historial',  label:'📋 Historial' },
];

export default function LoansManager() {
  const [tab, setTab] = useState('inventario');
  const [reload, setReload] = useState(0);

  useRealtime(['loan_request_new','loan_approved','loan_active','loan_returned','loan_rejected'], () => {
    setReload(r => r+1);
  });

  return (
    <BackDeskLayout title="Préstamos de equipos">
      {/* Tabs */}
      <div style={{ display:'flex', gap:0, marginBottom:24, borderBottom:'2px solid #E5E7EB' }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding:'10px 20px', border:'none', background:'none', cursor:'pointer',
            fontSize:13, fontWeight: tab===t.key?700:500,
            color: tab===t.key ? ACCENT : '#6B7280',
            borderBottom: tab===t.key ? `2px solid ${ACCENT}` : '2px solid transparent',
            marginBottom:-2, fontFamily:'inherit',
            transition:'color .15s',
          }}>{t.label}</button>
        ))}
      </div>

      {tab === 'inventario' && <TabInventario key={reload} onReload={() => setReload(r=>r+1)}/>}
      {tab === 'activos'    && <TabActivos    key={reload} onReload={() => setReload(r=>r+1)}/>}
      {tab === 'historial'  && <TabHistorial/>}
    </BackDeskLayout>
  );
}
